/**
 * QL-741 — portfolio_display's chart mode: the products' weights as a donut.
 * Vega draws no legend, so without the table under it the chart lists each
 * product with its color, weight and details button itself; with the table,
 * the table's swatches name the slices.
 */

import { Info } from "lucide-react";
import { useMemo } from "react";
import { PieChart } from "@/components/charts/pie-chart";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TABLEAU20_COLORS } from "@/lib/utils/vega-chart-shared";

type PortfolioDisplaySlice = {
  /** Shared with the product's table row. */
  key: string;
  /** Null for an unmatchable holding (QL-304): no details to open. */
  productId: string | null;
  name: string;
  /** Percent, 0–100. */
  weight: number;
  color: string;
};

/**
 * One slice per product, in table order, colored by position so a row's
 * swatch matches its slice. The tenant palette repeats past its last color.
 */
export function portfolioDisplaySlices(
  products: { key: string; id: string | null; name: string; weight: number }[],
  chartColors: string[] | null | undefined,
): PortfolioDisplaySlice[] {
  const palette = chartColors?.length ? chartColors : TABLEAU20_COLORS;
  return products.map((p, i) => ({
    key: p.key,
    productId: p.id,
    name: p.name,
    weight: p.weight,
    color: palette[i % palette.length],
  }));
}

type PortfolioDisplayChartProps = {
  slices: PortfolioDisplaySlice[];
  showLegend: boolean;
  language: string;
  onOpenInfo: (productId: string) => void;
  /** Why an unmatchable holding has no details; the table row says the same. */
  unmatchableTooltip: string;
};

export function PortfolioDisplayChart({
  slices,
  showLegend,
  language,
  onOpenInfo,
  unmatchableTooltip,
}: PortfolioDisplayChartProps) {
  // A zero weight has no arc to draw.
  const data = useMemo(
    () =>
      slices
        .filter((s) => s.weight > 0)
        .map((s) => ({ id: s.key, label: s.name, value: s.weight, color: s.color })),
    [slices],
  );

  return (
    <div className="space-y-4" data-testid="portfolio-display-chart">
      <div className="h-[260px]">
        <PieChart data={data} width={320} height={260} lang={language} />
      </div>
      {showLegend && (
        <ul className="space-y-1 text-sm">
          {slices.map((s) => (
            <li key={s.key} className="flex min-h-8 items-center gap-2">
              <span className="size-3 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
              <span className="min-w-0 flex-1">{s.name}</span>
              <span className="tabular-nums text-muted-foreground">{s.weight.toFixed(2)}%</span>
              <span className="w-8 shrink-0">
                {s.productId ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 text-muted-foreground hover:text-foreground"
                    onClick={() => onOpenInfo(s.productId as string)}
                    tooltip={s.name}
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                ) : (
                  // Same as the table row: a disabled button can't hover, so the span does.
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground"
                          disabled
                          tooltip={unmatchableTooltip}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{unmatchableTooltip}</TooltipContent>
                  </Tooltip>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
